import { useState, useEffect } from 'react';
import Login from './components/Login';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import ClientDashboard from './components/ClientDashboard';
import TRNBranchSelector from './components/TRNBranchSelector';
import { Toaster } from './components/ui/sonner';
import { TRN } from './types';
import { STORAGE_KEYS } from './constants';

export type UserRole = 'super-admin' | 'client' | null;

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  clientId?: string;
  selectedTRN?: string;
  selectedBranch?: string;
}

// Mock TRN data for demo purposes
const MOCK_TRNS: TRN[] = [
  {
    id: 'trn-001',
    trnNumber: '100123456700003',
    legalEntityName: 'ABC Trading LLC',
    status: 'active',
    registrationDate: '2020-01-15',
    branches: [
      {
        id: 'branch-001',
        branchCode: 'DXB-001',
        branchName: 'Dubai Main Office',
        address: 'Sheikh Zayed Road, Trade Centre 1',
        city: 'Dubai',
        emirate: 'Dubai',
        status: 'active',
        manager: 'Ahmed Al Mansoori'
      },
      {
        id: 'branch-002',
        branchCode: 'DXB-002',
        branchName: 'Dubai Marina Branch',
        address: 'Dubai Marina Mall, Ground Floor',
        city: 'Dubai',
        emirate: 'Dubai',
        status: 'active',
        manager: 'Fatima Al Zarooni'
      },
      {
        id: 'branch-003',
        branchCode: 'SHJ-001',
        branchName: 'Sharjah Branch',
        address: 'Al Khan Area, Office 205',
        city: 'Sharjah',
        emirate: 'Sharjah',
        status: 'active'
      }
    ]
  },
  {
    id: 'trn-002',
    trnNumber: '100234567800004',
    legalEntityName: 'XYZ Electronics Trading LLC',
    status: 'active',
    registrationDate: '2021-03-20',
    branches: [
      {
        id: 'branch-004',
        branchCode: 'AUH-001',
        branchName: 'Abu Dhabi Headquarters',
        address: 'Corniche Road, Al Markaziyah',
        city: 'Abu Dhabi',
        emirate: 'Abu Dhabi',
        status: 'active',
        manager: 'Mohammed Al Blooshi'
      },
      {
        id: 'branch-005',
        branchCode: 'AUH-002',
        branchName: 'Yas Island Branch',
        address: 'Yas Mall, Second Floor',
        city: 'Abu Dhabi',
        emirate: 'Abu Dhabi',
        status: 'active'
      }
    ]
  }
];

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [needsTRNSelection, setNeedsTRNSelection] = useState(false);

  useEffect(() => {
    // Check if TRN/Branch selection exists in localStorage
    const savedTRN = localStorage.getItem(STORAGE_KEYS.SELECTED_TRN);
    const savedBranch = localStorage.getItem(STORAGE_KEYS.SELECTED_BRANCH);
    
    // Only clients need TRN/Branch selection (TRNs are customer data)
    if (user && user.role === 'client' && (!savedTRN || !savedBranch)) {
      setNeedsTRNSelection(true);
    }
  }, [user]);

  const handleLogin = (userData: User) => {
    setUser(userData);
    // Only client users need to select TRN/Branch (their own company data)
    if (userData.role === 'client') {
      const savedTRN = localStorage.getItem(STORAGE_KEYS.SELECTED_TRN);
      const savedBranch = localStorage.getItem(STORAGE_KEYS.SELECTED_BRANCH);
      if (!savedTRN || !savedBranch) {
        setNeedsTRNSelection(true);
      }
    }
  };

  const handleLogout = () => {
    setUser(null);
    setNeedsTRNSelection(false);
    localStorage.removeItem(STORAGE_KEYS.SELECTED_TRN);
    localStorage.removeItem(STORAGE_KEYS.SELECTED_BRANCH);
  };

  const handleTRNBranchSelect = (trnId: string, branchId: string) => {
    localStorage.setItem(STORAGE_KEYS.SELECTED_TRN, trnId);
    localStorage.setItem(STORAGE_KEYS.SELECTED_BRANCH, branchId);
    setUser({
      ...user!,
      selectedTRN: trnId,
      selectedBranch: branchId
    });
    setNeedsTRNSelection(false);
  };

  const handleTRNBranchSwitch = (trnId: string, branchId: string) => {
    localStorage.setItem(STORAGE_KEYS.SELECTED_TRN, trnId);
    localStorage.setItem(STORAGE_KEYS.SELECTED_BRANCH, branchId);
    setUser({
      ...user!,
      selectedTRN: trnId,
      selectedBranch: branchId
    });
  };

  if (!user) {
    return (
      <>
        <Login onLogin={handleLogin} />
        <Toaster />
      </>
    );
  }

  if (needsTRNSelection && user.role === 'client') {
    return (
      <>
        <TRNBranchSelector trns={MOCK_TRNS} onSelect={handleTRNBranchSelect} />
        <Toaster />
      </>
    );
  }

  return (
    <>
      {user.role === 'super-admin' ? (
        <SuperAdminDashboard user={user} onLogout={handleLogout} />
      ) : (
        <ClientDashboard 
          user={user} 
          onLogout={handleLogout}
          trns={MOCK_TRNS}
          onTRNBranchSwitch={handleTRNBranchSwitch}
        />
      )}
      <Toaster />
    </>
  );
}

export default App;
